import React from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface EnhancedPaginationControlProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export const EnhancedPaginationControl: React.FC<
  EnhancedPaginationControlProps
> = ({ totalPages, currentPage, onPageChange, className = "" }) => {
  if (totalPages <= 1) return null;

  return (
    <div className={`flex-1 flex justify-center ${className}`}>
      <Pagination>
        <PaginationContent>
          {/* First page button */}
          <PaginationItem>
            <button
              className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                currentPage === 0 ? "pointer-events-none opacity-50" : ""
              }`}
              onClick={() => onPageChange(0)}
              disabled={currentPage === 0}
              aria-label="First page"
              type="button"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          </PaginationItem>

          {/* Previous page button */}
          {currentPage > 0 ? (
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(0, currentPage - 1))}
              className="gap-0 px-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </PaginationPrevious>
          ) : (
            <PaginationPrevious
              onClick={() => {}}
              className="pointer-events-none opacity-50 gap-0 px-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </PaginationPrevious>
          )}

          {/* Page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNumber = currentPage <= 2 ? i : currentPage + i - 2;
            if (pageNumber >= totalPages) return null;
            return (
              <PaginationItem key={pageNumber}>
                <PaginationLink
                  onClick={() => onPageChange(pageNumber)}
                  isActive={currentPage === pageNumber}
                >
                  {pageNumber + 1}
                </PaginationLink>
              </PaginationItem>
            );
          })}

          {/* Next page button */}
          {currentPage < totalPages - 1 ? (
            <PaginationNext
              onClick={() =>
                onPageChange(Math.min(totalPages - 1, currentPage + 1))
              }
              className="gap-0 px-0"
            >
              <ChevronRight className="h-4 w-4" />
            </PaginationNext>
          ) : (
            <PaginationNext
              onClick={() => {}}
              className="pointer-events-none opacity-50 gap-0 px-0"
            >
              <ChevronRight className="h-4 w-4" />
            </PaginationNext>
          )}

          {/* Last page button */}
          <PaginationItem>
            <button
              className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                currentPage === totalPages - 1
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
              onClick={() => onPageChange(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              aria-label="Last page"
              type="button"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
